import React, { useMemo, useState } from 'react';
import { Button, Modal, Space, Table, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { getFolderAndDocList } from '../../../api/data-project-folder';
import { linkCompileDocument, linkCompileFolder } from '@/pages/documentCompile/api/data-project-doc';
import { ReactComponent as ZlbzFolderIcon } from '@/assets/svg/zlbz-folder.svg';
import { ReactComponent as ZlbzArticleIcon } from '@/assets/svg/zlbz-article.svg';
import type { FolderItem } from '@/pages/documentCompile/store/useStore';
import useStore from '@/pages/documentCompile/store/useStore';

interface BreadcrumbItem {
  folderId: string;
  folderName: string;
  parentId: string | null;
}

interface FileRow {
  id: string;
  fileName: string;
  isFolder?: boolean;
  fileType?: string;
  count?: number;
  size?: number;
  fileCount?: number;
  fileSize?: number;
}

interface LinkFromOtherFolderModalProps {
  open: boolean;
  onCancel: () => void;
  onSuccess: () => void;
  projectId: string;
  /** 目标：引用到当前目录（parentId） */
  targetParentFolderId: number;
  /** 排除：不展示当前目录（根目录/当前进入的目录） */
  excludeFolderId: number;
  folderList: FolderItem[];
  folderListLoading: boolean;
  requestId: string;
}

type Step = 'folder' | 'file';

const LinkFromOtherFolderModal: React.FC<LinkFromOtherFolderModalProps> = ({
  open,
  onCancel,
  onSuccess,
  projectId,
  targetParentFolderId,
  excludeFolderId,
  folderList,
  folderListLoading,
  requestId,
}) => {
  const [step, setStep] = useState<Step>('folder');
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);

  const [currentFolderId, setCurrentFolderId] = useState<string | null>(null);
  const [breadcrumbs, setBreadcrumbs] = useState<BreadcrumbItem[]>([]);
  const [fileList, setFileList] = useState<FileRow[]>([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);

  const availableFolders = useMemo(() => {
    return (folderList || []).filter((i) => String(i.folderId) !== String(excludeFolderId));
  }, [excludeFolderId, folderList]);

  const resetToFolderStep = () => {
    setStep('folder');
    setSubmitting(false);
    setLoading(false);
    setCurrentFolderId(null);
    setBreadcrumbs([]);
    setFileList([]);
    setSelectedRowKeys([]);
  };

  const fetchFiles = async (folderId: string) => {
    try {
      setLoading(true);
      setSelectedRowKeys([]);
      const params = {
        projectId,
        parentId: folderId,
        pageIndex: 1,
        pageSize: 100,
      };
      const result = await getFolderAndDocList(params);
      const transformed = (result || []).map((item: any) => ({
        ...item,
        id: String(item.id ?? item.folderId),
        fileName: item.fileName || item.folderName || item.name,
        fileType: item.type || (item.fileName ? 'file' : 'folder'),
        fileSize: item.fileSize || 0,
        isFolder: item.folder,
        fileCount: item.fileCount || 0,
      })) as FileRow[];
      setFileList(transformed);
    } catch (e) {
      message.error('获取引用文件列表失败');
    } finally {
      setLoading(false);
    }
  };

  const enterFolder = (folderId: string, folderName: string, parentId: string | null) => {
    const nextBreadcrumbs =
      breadcrumbs.length === 0
        ? [{ folderId, folderName, parentId }]
        : [...breadcrumbs, { folderId, folderName, parentId }];
    setStep('file');
    setCurrentFolderId(folderId);
    setBreadcrumbs(nextBreadcrumbs);
    setSelectedRowKeys([]);
    fetchFiles(folderId);
  };

  const backInModal = () => {
    if (step !== 'file') return;
    if (breadcrumbs.length <= 1) {
      resetToFolderStep();
      return;
    }
    const nextBreadcrumbs = breadcrumbs.slice(0, -1);
    const parent = nextBreadcrumbs[nextBreadcrumbs.length - 1];
    setBreadcrumbs(nextBreadcrumbs);
    setCurrentFolderId(parent.folderId);
    setSelectedRowKeys([]);
    fetchFiles(parent.folderId);
  };

  const handleOk = async () => {
    if (step !== 'file' || !currentFolderId) return;
    if (!selectedRowKeys.length) return;
    const fileMap = new Map(fileList.map((item) => [String(item.id), item]));
    const folderIds: string[] = [];
    const docIds: string[] = [];

    selectedRowKeys.forEach((key) => {
      const item = fileMap.get(String(key));
      if (!item) return;
      if (item.isFolder) folderIds.push(String(key));
      else docIds.push(String(key));
    });

    // const requests: Promise<unknown>[] = [];
    const params = {
      projectId,
      targetParentId: targetParentFolderId,
      sourceParentId: currentFolderId,
      docIds: [],
      folderIds: [],
    }
    if (folderIds.length) {
      params.folderIds = folderIds
      // requests.push(
      //   linkCompileDocument({
      //     projectId,
      //     sourceFolderIds: folderIds,
      //     targetParentFolderId,
      //   }, requestId),
      // );
    }
    if (docIds.length) {
      params.docIds = docIds
      // requests.push(
      //   Promise.all(
      //     docIds.map((docId) =>
      //       linkCompileDocument({
      //         projectId,
      //         docId,
      //         parentId: targetParentFolderId,
      //       }, requestId),
      //     ),
      //   ),
      // );
    }

    try {
      setSubmitting(true);
      // await Promise.all(requests);
      await linkCompileDocument(params, requestId);
      message.success('引用成功');
      onSuccess();
      resetToFolderStep();
    } catch (e) {
      message.error('引用失败');
    } finally {
      setSubmitting(false);
    }
  };

  const folderColumns: ColumnsType<FolderItem> = [
    {
      title: '资料目录/名称',
      dataIndex: 'folderName',
      key: 'folderName',
      render: (text: string, record: FolderItem) => (
        <Space style={{ display: 'flex', alignItems: 'center', margin: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', margin: 0 }}>
            <ZlbzFolderIcon />
          </div>
          <a
            onClick={() => enterFolder(String(record.folderId), record.folderName, record.parentId == null ? null : String(record.parentId))}
            style={{ color: '#1890ff' }}
          >
            {text}
          </a>
        </Space>
      ),
    },
    {
      title: '文件个数',
      dataIndex: 'fileCount',
      key: 'fileCount',
      render: (count?: number) => `${count || 0}个`,
    },
    {
      title: '文件大小',
      dataIndex: 'fileSize',
      key: 'fileSize',
      render: (size?: number) => {
        const s = size || 0;
        if (s < 1024) return `${s}B`;
        if (s < 1024 * 1024) return `${(s / 1024).toFixed(1)}KB`;
        return `${(s / (1024 * 1024)).toFixed(1)}MB`;
      },
    },
    {
      title: '文件类型',
      dataIndex: 'fileType',
      key: 'fileType',
      render: () => '文件夹',
    },
  ];

  const fileColumns: ColumnsType<FileRow> = [
    {
      title: '资料目录/名称',
      dataIndex: 'fileName',
      key: 'fileName',
      render: (text: string, record: FileRow) => (
        <Space style={{ display: 'flex', alignItems: 'center', margin: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', margin: 0 }}>
            {record.isFolder ? <ZlbzFolderIcon /> : <ZlbzArticleIcon />}
          </div>
          {record.isFolder ? (
            <a
              style={{ color: '#1890ff' }}
              onClick={() => enterFolder(record.id, record.fileName, currentFolderId)}
            >
              {text}
            </a>
          ) : (
            <span>{text}</span>
          )}
        </Space>
      ),
    },
    {
      title: '文件个数',
      dataIndex: 'count',
      key: 'count',
      render: (count: number | undefined, record: FileRow) => {
        if (record.isFolder) {
          const displayCount = count || 0;
          return <span style={{ color: displayCount === 0 ? '#ff4d4f' : 'inherit' }}>{displayCount}个</span>;
        }
        return '1个';
      },
    },
    {
      title: '文件大小',
      dataIndex: 'size',
      key: 'size',
      render: (size?: number) => {
        const s = size || 0;
        if (s < 1024) return `${s}B`;
        if (s < 1024 * 1024) return `${(s / 1024).toFixed(1)}KB`;
        return `${(s / (1024 * 1024)).toFixed(1)}MB`;
      },
    },
    {
      title: '文件类型',
      dataIndex: 'fileType',
      key: 'fileType',
      render: (_: string | undefined, record: FileRow) => (record.isFolder ? '文件夹' : '文件'),
    },
  ];

  return (
    <Modal
      title={step === 'folder' ? '引用 - 选择目录' : '引用 - 选择文件/文件夹'}
      open={open}
      onCancel={() => {
        onCancel();
        resetToFolderStep();
      }}
      onOk={handleOk}
      okText="确定"
      cancelText="取消"
      confirmLoading={submitting}
      okButtonProps={{ disabled: step !== 'file' || selectedRowKeys.length === 0 }}
      width={900}
      destroyOnClose
      afterClose={resetToFolderStep}
    >
      {step === 'folder' ? (
        <Table
          columns={folderColumns}
          dataSource={availableFolders}
          rowKey="folderId"
          pagination={false}
          bordered
          size="small"
          loading={folderListLoading}
          scroll={{ x: 'max-content' }}
        />
      ) : (
        <div>
          <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Space>
              <Button type="link" size="small" icon={<ArrowLeftOutlined />} onClick={backInModal}>
                返回
              </Button>
              <span style={{ color: '#666' }}>{breadcrumbs.map((i) => i.folderName).join(' / ')}</span>
            </Space>
            <span style={{ color: '#999' }}>仅需关注当前层级勾选</span>
          </div>
          <Table
            rowSelection={{
              selectedRowKeys,
              onChange: (keys: React.Key[]) => setSelectedRowKeys(keys),
            }}
            columns={fileColumns}
            dataSource={fileList}
            loading={loading}
            rowKey="id"
            pagination={false}
            scroll={{ x: 'max-content' }}
            bordered
            size="small"
          />
        </div>
      )}
    </Modal>
  );
};

export default LinkFromOtherFolderModal;

